import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { commission as commissionOf } from '@wasity/shared';
import { Button, Card, ConfirmDialog, DateText, Dialog, ErrorNotice, Field, Input, Notice, PageHeader, Select, Skeleton, Table, Textarea } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { actAs, api } from '@/lib/api';
import { errorText } from '@/lib/errors';
import { money, parseSar } from '@/lib/format';
import type { AuditRow, BrDetail } from '@/lib/types';
import { BrStepper, PersonCard, PropertyCard, RequestTimeline, SlotLine } from '@/features/shared/RequestParts';
import { AdminBrChip, ratePct, realEmail, settingValue, useSettings } from './common';

type UpdateKind = 'contract_signed' | 'licensed' | 'listed' | 'note';

export function AdminRequestDetail() {
  const { id = '' } = useParams();
  const { t, locale, localTitle } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const detail = useQuery({ queryKey: ['admin', 'br', id], queryFn: () => api.get<BrDetail>(`/api/admin/broker-requests/${id}`) });
  const audit = useQuery({ queryKey: ['admin', 'audit', 'BR', id], queryFn: () => api.get<{ items: AuditRow[] }>(`/api/admin/audit?kind=BR&id=${id}`) });
  const settings = useSettings();
  const [dialog, setDialog] = useState<'cancel' | 'commission' | 'update' | 'offer' | 'reject' | null>(null);
  const [updateKind, setUpdateKind] = useState<UpdateKind>('note');
  const [form, setForm] = useState({ text: '', licenceNo: '', platform: 'Aqar', url: '', amount: '', terms: '' });
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin'] });
    setDialog(null);
    setError(null);
  };
  const fail = (e: unknown) => setError((e as { code?: string }).code ?? 'error');
  const brokerId = detail.data?.request.brokerUserId ?? '';
  const asBroker = { 'x-act-as-user': brokerId };
  const cancel = useMutation({ mutationFn: (reason: string) => api.post(`/api/admin/broker-requests/${id}/cancel`, { reason: reason || undefined }), onSuccess: refresh, onError: fail });
  const confirmCommission = useMutation({ mutationFn: () => api.post(`/api/admin/broker-requests/${id}/commission/confirm`), onSuccess: refresh, onError: fail });
  const postUpdate = useMutation({
    mutationFn: () => {
      const body = updateKind === 'note' ? { kind: 'note', text: form.text } : updateKind === 'licensed' ? { kind: 'licensed', licenceNo: form.licenceNo || undefined } : updateKind === 'listed' ? { kind: 'listed', platform: form.platform, url: form.url || undefined } : { kind: 'contract_signed' };
      return api.post(`/api/broker/requests/${id}/updates`, body, asBroker);
    },
    onSuccess: refresh,
    onError: fail,
  });
  const postOffer = useMutation({ mutationFn: () => api.post(`/api/broker/requests/${id}/offers`, { amountHalalas: parseSar(form.amount), terms: form.terms || undefined }, asBroker), onSuccess: refresh, onError: fail });
  const decide = useMutation({ mutationFn: (input: { offerId: string; accept: boolean; reason?: string }) => api.post(`/api/app/broker-requests/${id}/offers/${input.offerId}/decide`, { accept: input.accept, reason: input.reason }), onSuccess: refresh, onError: fail });

  if (detail.isLoading) return <Skeleton lines={10} />;
  if (detail.error || !detail.data) return <ErrorNotice error={detail.error} retry={() => void detail.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const d = detail.data;
  const br = d.request;
  const rate = settingValue<number>(settings.data?.items, 'commission.rate', 0.005);
  const pendingOffer = d.offers.find((o) => o.status === 'pending');
  const terminal = br.status === 'completed' || br.status === 'cancelled';
  const afterContract = ['contract_signed', 'licensed', 'offer_pending'].includes(br.status);
  const names = { ...(d.broker ? { [d.broker.id]: d.broker.name } : {}), ...(d.owner ? { [d.owner.id]: d.owner.name } : {}) };
  const enterAsBroker = () => {
    if (!d.broker) return;
    actAs.set({ id: d.broker.id, name: d.broker.name, role: 'broker' });
    navigate(`/broker/requests/${id}`);
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        crumbs={[{ to: '/admin/broker-requests', label: t('nav.brokerRequests') }, { label: br.number }]}
        title={localTitle(d)}
        chip={<AdminBrChip br={br} />}
        meta={<span><span className="num">{br.number}</span> · {t('app.created')} <DateText at={br.createdAt} /></span>}
        actions={
          !terminal ? (
            <>
              {afterContract ? <span className="text-[13px] text-muted max-w-[36ch] self-center">{t('admin.request.noReassign')}</span> : null}
              <Button variant="danger" className="min-h-9 text-[14px]" onClick={() => setDialog('cancel')}>{t('admin.request.cancel')}</Button>
            </>
          ) : undefined
        }
      />
      <Card><BrStepper status={br.status} /></Card>
      {error ? <Notice tone="error">{errorText(error, t)}</Notice> : null}
      <div className="grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-6 items-start">
        <div className="flex flex-col gap-4">
          {br.status === 'new' ? (
            <Notice tone="warning" title={br.returnedAt ? t('status.expiredReassign') : t('status.awaitingAssignment')}>
              <Link to={`/admin/queue?select=${br.id}`}>{t('admin.request.assignNow')}</Link>
            </Notice>
          ) : null}
          {br.status === 'cancelled' ? <Notice tone="info" title={t('admin.request.cancelled')}>{t('admin.request.cancelledBy', { when: br.cancelledAt ? formatRelative(br.cancelledAt, locale) : '', reason: br.cancelReason ?? '—' })}</Notice> : null}
          {pendingOffer && br.status === 'offer_pending' ? (
            <Notice tone="info" title={t('admin.request.offerNotice')}>
              <p>{t('admin.request.offerNoticeBody', { amount: money(pendingOffer.amountHalalas, locale), when: formatRelative(pendingOffer.createdAt, locale) })}</p>
              <div className="flex gap-2 mt-3">
                <Button variant="accent" className="min-h-9 text-[14px]" loading={decide.isPending} onClick={() => decide.mutate({ offerId: pendingOffer.id, accept: true })}>{t('admin.request.acceptOffer')}</Button>
                <Button variant="secondary" className="min-h-9 text-[14px]" onClick={() => setDialog('reject')}>{t('admin.request.rejectOffer')}</Button>
              </div>
            </Notice>
          ) : null}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2>{t('admin.request.updates')}</h2>
              {d.broker && !terminal ? (
                <div className="flex gap-2">
                  <Button variant="secondary" className="min-h-9 text-[14px]" onClick={() => setDialog('update')}>{t('admin.request.addUpdate')}</Button>
                  {br.status === 'licensed' ? <Button variant="secondary" className="min-h-9 text-[14px]" onClick={() => setDialog('offer')}>{t('admin.request.addOffer')}</Button> : null}
                </div>
              ) : null}
            </div>
            <RequestTimeline events={d.events} viewer="admin" names={names} />
          </Card>
          <Card>
            <h2 className="mb-3">{t('admin.request.audit')}</h2>
            {audit.data?.items.length ? (
              <Table>
                <tbody>
                  {audit.data.items.map((a) => (
                    <tr key={a.id}>
                      <td className="text-muted min-w-[120px]"><DateText at={a.createdAt} wrap /></td>
                      <td className="ltr num">{realEmail(a.actorEmail) ?? a.actorName ?? '—'}</td>
                      <td className="num">{a.action}</td>
                      <td className="text-muted num text-[13px] max-w-[14ch] truncate">{a.after ? JSON.stringify(a.after).slice(0, 80) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted text-[14px]">{t('app.none')}</p>
            )}
          </Card>
        </div>
        <aside className="flex flex-col gap-3">
          <PropertyCard property={d.property} photos={d.photos} />
          <PersonCard title={t('admin.request.ownerCard')} person={d.owner} action={d.owner ? <Link to={`/admin/users/${d.owner.id}`} className="text-[14px]">{t('app.account')}</Link> : null} />
          {br.adminNotes ? (
            <Card>
              {/* Written when assigning, shown to the broker on their Incoming card — and, until now,
                  on no admin screen at all, so the admin could not see what they had told them. */}
              <h2 className="mb-2">{t('admin.request.adminNoteCard')}</h2>
              <p className="text-[14px] text-fg-2">«{br.adminNotes}»</p>
            </Card>
          ) : null}
          <PersonCard title={t('admin.request.brokerCard')} person={d.broker} subtitle={d.broker ? [d.broker.officeName, d.broker.licenceNo ? `${locale === 'ar' ? 'فال' : 'FAL'} ${d.broker.licenceNo}` : null].filter(Boolean).join(' · ') : undefined} action={d.broker ? <Link to={`/admin/users/${d.broker.id}`} className="text-[14px]">{t('app.account')}</Link> : null}>
            {d.brokerSlots ? <SlotLine slots={d.brokerSlots} className="mt-3" /> : null}
            {!d.broker ? <p className="text-muted text-[14px]">{t('admin.request.noBroker')}</p> : null}
          </PersonCard>
          <Card lift>
            <h2 className="mb-2">{t('admin.request.commissionCard')}</h2>
            <p className="text-[14px] text-fg-2">{t('admin.request.commissionCalc', { rate: ratePct(rate) })}</p>
            {pendingOffer ? (
              <div className="flex justify-between mt-2.5"><span>{t('admin.request.onCurrentOffer')}</span><b className="num">{money(commissionOf(pendingOffer.amountHalalas, rate), locale)}</b></div>
            ) : br.commissionHalalas != null ? (
              <div className="flex justify-between mt-2.5"><span>{t('admin.request.dueAmount')}</span><b className="num">{money(br.commissionHalalas, locale)}</b></div>
            ) : null}
            <Button variant="secondary" className="w-full mt-3" disabled={br.commissionStatus !== 'due'} onClick={() => setDialog('commission')}>{t('admin.request.confirmCommission')}</Button>
            {br.commissionStatus !== 'due' ? <p className="text-muted text-[13px] mt-2">{t('admin.request.confirmCommissionHint')}</p> : null}
          </Card>
          {d.broker ? (
            <Card>
              <h2 className="mb-2">{t('admin.request.enterAsBroker')}</h2>
              <p className="text-[14px] text-fg-2">{t('admin.request.enterAsBrokerHint', { name: d.broker.name })}</p>
              <Button variant="secondary" className="w-full mt-3" onClick={enterAsBroker}>{t('admin.request.openBrokerPanel')}</Button>
            </Card>
          ) : null}
        </aside>
      </div>

      <ConfirmDialog open={dialog === 'cancel'} onClose={() => setDialog(null)} title={t('admin.request.cancelTitle', { number: br.number })} body={t('admin.request.cancelBody')} confirmLabel={t('admin.request.cancel')} cancelLabel={t('app.back')} danger reason={{ label: t('app.reasonOptional') }} loading={cancel.isPending} onConfirm={(r) => cancel.mutate(r)} />
      <ConfirmDialog open={dialog === 'commission'} onClose={() => setDialog(null)} title={t('admin.request.confirmCommissionTitle', { amount: money(br.commissionHalalas, locale) })} body={t('admin.request.confirmCommissionBody')} confirmLabel={t('app.confirm')} cancelLabel={t('app.back')} loading={confirmCommission.isPending} onConfirm={() => confirmCommission.mutate()} />
      <ConfirmDialog open={dialog === 'reject'} onClose={() => setDialog(null)} title={t('admin.request.rejectOffer')} confirmLabel={t('admin.request.rejectOffer')} cancelLabel={t('app.back')} danger reason={{ label: t('app.reason'), required: true }} loading={decide.isPending} onConfirm={(r) => pendingOffer && decide.mutate({ offerId: pendingOffer.id, accept: false, reason: r })} />
      <Dialog open={dialog === 'update'} onClose={() => setDialog(null)} title={t('admin.request.addUpdate')}>
        <div className="flex flex-col gap-4">
          <Field label={t('app.kind')} htmlFor="upd-kind">
            <Select id="upd-kind" value={updateKind} onChange={(e) => setUpdateKind(e.target.value as UpdateKind)}>
              <option value="note">{t('broker.request.noteToOwner')}</option>
              {br.status === 'active' ? <option value="contract_signed">{t('broker.request.contractSigned')}</option> : null}
              {br.status === 'contract_signed' ? <option value="licensed">{t('broker.request.licenceIssued')}</option> : null}
              {['licensed', 'offer_pending'].includes(br.status) ? <option value="listed">{t('broker.request.listedOn')}</option> : null}
            </Select>
          </Field>
          {updateKind === 'note' ? <Field label={t('broker.request.noteText')} htmlFor="upd-text"><Textarea id="upd-text" rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} /></Field> : null}
          {updateKind === 'licensed' ? <Field label={t('broker.request.licenceNo')} htmlFor="upd-lic"><Input id="upd-lic" dir="ltr" className="num" value={form.licenceNo} onChange={(e) => setForm({ ...form, licenceNo: e.target.value })} /></Field> : null}
          {updateKind === 'listed' ? (
            <>
              <Field label={t('broker.request.platform')} htmlFor="upd-platform"><Input id="upd-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} /></Field>
              <Field label={t('broker.request.url')} htmlFor="upd-url"><Input id="upd-url" dir="ltr" className="num" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" /></Field>
            </>
          ) : null}
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setDialog(null)}>{t('app.back')}</Button>
            <Button variant="primary" loading={postUpdate.isPending} disabled={updateKind === 'note' && !form.text.trim()} onClick={() => postUpdate.mutate()}>{t('app.add')}</Button>
          </div>
        </div>
      </Dialog>
      <Dialog open={dialog === 'offer'} onClose={() => setDialog(null)} title={t('admin.request.addOffer')}>
        <div className="flex flex-col gap-4">
          <Field label={t('broker.request.offerAmount')} htmlFor="offer-amount"><Input id="offer-amount" dir="ltr" className="num" inputMode="numeric" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
          <Field label={t('broker.request.offerTerms')} htmlFor="offer-terms"><Textarea id="offer-terms" rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></Field>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setDialog(null)}>{t('app.back')}</Button>
            <Button variant="primary" loading={postOffer.isPending} disabled={!parseSar(form.amount)} onClick={() => postOffer.mutate()}>{t('broker.request.sendOffer')}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
